import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Sparkles, Send, Loader2, Bot, User as UserIcon, Lightbulb, FileText,
  AlertTriangle, Trophy, GraduationCap, ShieldCheck, Award, Apple,
  TrendingDown, BookOpen, Crown, Medal, Users as UsersIcon,
} from "lucide-react";
import { toast } from "sonner";
import { localName, localFullName } from "../lib/localName";

export default function AiAssistantPage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const canEvaluateTeachers = ["super_admin", "school_admin"].includes(user?.role);
  const [tab, setTab] = useState("chat");

  // Chat
  const [sessionId] = useState(() => `chat-${Math.random().toString(36).slice(2, 10)}`);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Common
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [busy, setBusy] = useState(false);

  // Tab payloads
  const [summary, setSummary] = useState("");
  const [recs, setRecs] = useState("");
  const [obstacles, setObstacles] = useState(null);
  const [topPerformers, setTopPerformers] = useState(null);
  const [teacherEval, setTeacherEval] = useState(null);

  useEffect(() => {
    api.get("/schools").then((r) => {
      setSchools(r.data || []);
      if ((r.data || []).length) {
        const demo = (r.data || []).find((s) => s.id === "demo-school-1");
        setSchoolId(demo ? demo.id : r.data[0].id);
      }
    });
    api.get("/students").then((r) => setStudents(r.data || []));
  }, []);
  useEffect(() => {
    if (schoolId) api.get(`/teachers?school_id=${schoolId}`).then((r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setTeachers(items);
    });
  }, [schoolId]);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, sending]);

  const send = async () => {
    if (!input.trim()) return;
    const text = input.trim();
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/teacher-assistant", { session_id: sessionId, message: text, lang });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error("AI " + (e.response?.data?.detail || e.message));
    }
    setSending(false);
  };

  const runSummary = async () => {
    if (!studentId) return;
    setBusy(true); setSummary("");
    try {
      const { data } = await api.post("/ai/student-summary", { student_id: studentId, lang });
      setSummary(data.summary);
    } catch (e) { toast.error("AI " + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };
  const runRecs = async () => {
    if (!studentId) return;
    setBusy(true); setRecs("");
    try {
      const { data } = await api.post("/ai/teaching-recommendations", { student_id: studentId, lang });
      setRecs(data.recommendations);
    } catch (e) { toast.error("AI " + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };
  const runObstacles = async () => {
    if (!schoolId) return;
    setBusy(true); setObstacles(null);
    try {
      const { data } = await api.post("/ai/learning-obstacles", { school_id: schoolId, lang });
      setObstacles(data);
    } catch (e) { toast.error("AI " + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };
  const runTopPerformers = async () => {
    if (!schoolId) return;
    setBusy(true); setTopPerformers(null);
    try {
      const { data } = await api.get(`/ai/top-performers?school_id=${schoolId}&days=30&limit=5`);
      setTopPerformers(data);
    } catch (e) { toast.error((e.response?.data?.detail || e.message)); }
    setBusy(false);
  };
  const runTeacherEval = async () => {
    if (!teacherId) return;
    setBusy(true); setTeacherEval(null);
    try {
      const { data } = await api.post("/ai/teacher-evaluation", { teacher_id: teacherId, lang });
      setTeacherEval(data);
    } catch (e) { toast.error("AI " + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground grid place-items-center">
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t("ai_assistant")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("ai_assistant_subtitle")}</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="chat" data-testid="ai-tab-chat"><Bot className="w-4 h-4 me-2" />{t("ai_chat")}</TabsTrigger>
          <TabsTrigger value="summary" data-testid="ai-tab-summary"><FileText className="w-4 h-4 me-2" />{t("ai_summary")}</TabsTrigger>
          <TabsTrigger value="recs" data-testid="ai-tab-recs"><Lightbulb className="w-4 h-4 me-2" />{t("ai_recommendations")}</TabsTrigger>
          <TabsTrigger value="obstacles" data-testid="ai-tab-obstacles"><AlertTriangle className="w-4 h-4 me-2" />{t("ai_obstacles")}</TabsTrigger>
          <TabsTrigger value="top" data-testid="ai-tab-top"><Trophy className="w-4 h-4 me-2" />{t("ai_top_performers")}</TabsTrigger>
          {canEvaluateTeachers && (
            <TabsTrigger value="evaluation" data-testid="ai-tab-eval"><BookOpen className="w-4 h-4 me-2" />{t("ai_teacher_eval")}</TabsTrigger>
          )}
        </TabsList>

        {/* CHAT */}
        <TabsContent value="chat">
          <Card className="border shadow-none flex flex-col h-[70vh]">
            <div ref={scrollRef} className="flex-1 p-4 overflow-y-auto space-y-3">
              {msgs.length === 0 && <div className="text-center text-muted-foreground text-sm py-12">{t("ai_chat_hint")}</div>}
              {msgs.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`ai-msg-${m.role}-${i}`}>
                  <div className={`w-8 h-8 shrink-0 rounded-lg grid place-items-center ${m.role === "user" ? "bg-secondary/20 text-secondary" : "bg-primary/10 text-primary"}`}>
                    {m.role === "user" ? <UserIcon className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>
                  <div className={`max-w-[75%] rounded-lg px-4 py-2 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex gap-3 items-center text-muted-foreground text-sm">
                  <div className="w-8 h-8 rounded-lg grid place-items-center bg-primary/10 text-primary"><Bot className="w-4 h-4" /></div>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("ai_thinking")}
                </div>
              )}
            </div>
            <div className="border-t border-border p-3 flex gap-2">
              <Input
                placeholder={t("type_a_message")}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                data-testid="ai-chat-input"
              />
              <Button onClick={send} disabled={sending || !input.trim()} data-testid="ai-chat-send">
                <Send className="w-4 h-4 me-1 lucide-rotate-rtl" />{t("send")}
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* SUMMARY */}
        <TabsContent value="summary">
          <Card className="border shadow-none p-6 space-y-4">
            <div className="flex gap-3 flex-wrap items-end">
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger className="w-full sm:w-80" data-testid="ai-summary-student"><SelectValue placeholder={t("students")} /></SelectTrigger>
                <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{localFullName(s, lang)}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={runSummary} disabled={!studentId || busy} data-testid="ai-summary-run">
                {busy ? <Loader2 className="w-4 h-4 animate-spin me-1" /> : <Sparkles className="w-4 h-4 me-1" />}{t("generate")}
              </Button>
            </div>
            {summary && (
              <div className="border-t border-border pt-4 whitespace-pre-wrap text-sm leading-relaxed" data-testid="ai-summary-output">{summary}</div>
            )}
          </Card>
        </TabsContent>

        {/* RECS */}
        <TabsContent value="recs">
          <Card className="border shadow-none p-6 space-y-4">
            <div className="flex gap-3 flex-wrap items-end">
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger className="w-full sm:w-80" data-testid="ai-recs-student"><SelectValue placeholder={t("students")} /></SelectTrigger>
                <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{localFullName(s, lang)}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={runRecs} disabled={!studentId || busy} data-testid="ai-recs-run">
                {busy ? <Loader2 className="w-4 h-4 animate-spin me-1" /> : <Sparkles className="w-4 h-4 me-1" />}{t("generate")}
              </Button>
            </div>
            {recs && (
              <div className="border-t border-border pt-4 whitespace-pre-wrap text-sm leading-relaxed" data-testid="ai-recs-output">{recs}</div>
            )}
          </Card>
        </TabsContent>

        {/* OBSTACLES */}
        <TabsContent value="obstacles">
          <Card className="border shadow-none p-6 space-y-4">
            <div className="flex gap-3 flex-wrap items-end">
              <SchoolPicker value={schoolId} onChange={setSchoolId} schools={schools} t={t} lang={lang} />
              <Button onClick={runObstacles} disabled={!schoolId || busy} data-testid="ai-obstacles-run">
                {busy ? <Loader2 className="w-4 h-4 animate-spin me-1" /> : <AlertTriangle className="w-4 h-4 me-1" />}{t("analyze")}
              </Button>
              <span className="text-xs text-muted-foreground">{t("obstacles_hint")}</span>
            </div>
            {obstacles && (
              <div className="space-y-4 pt-2 border-t border-border">
                <div className="grid sm:grid-cols-3 gap-3">
                  <StatTile icon={TrendingDown} value={obstacles.struggling_count}
                            label={t("struggling_students")} accent="rose" />
                  <StatTile icon={GraduationCap} value={`<${obstacles.thresholds?.min_grade || 60}%`}
                            label={t("min_passing_grade")} />
                  <StatTile icon={ShieldCheck} value={`<${obstacles.thresholds?.min_attendance || 85}%`}
                            label={t("min_attendance")} />
                </div>
                {obstacles.students?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">{t("struggling_students")} ({obstacles.students.length})</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-3 py-2 text-start">{t("name")}</th>
                            <th className="px-3 py-2 text-end">{t("avg_grade")}</th>
                            <th className="px-3 py-2 text-end">{t("attendance")}</th>
                            <th className="px-3 py-2 text-end">{t("behavior_score")}</th>
                            <th className="px-3 py-2 text-start">{t("reasons")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {obstacles.students.map((s) => (
                            <tr key={s.student_id} className="border-t border-border" data-testid={`obstacle-${s.student_id}`}>
                              <td className="px-3 py-2">{s.name}</td>
                              <td className="px-3 py-2 text-end font-mono">{s.avg_grade ?? "—"}{s.avg_grade != null && "%"}</td>
                              <td className="px-3 py-2 text-end font-mono">{s.attendance_pct ?? "—"}{s.attendance_pct != null && "%"}</td>
                              <td className={`px-3 py-2 text-end font-mono ${s.behavior_net < 0 ? "text-rose-600" : ""}`}>{s.behavior_net}</td>
                              <td className="px-3 py-2 text-xs text-muted-foreground">{s.reasons.join(" · ")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {obstacles.ai_analysis && (
                  <div className="border-t border-border pt-3">
                    <h4 className="font-semibold mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> {t("ai_analysis")}</h4>
                    <div className="whitespace-pre-wrap text-sm leading-relaxed" data-testid="obstacles-ai-output">{obstacles.ai_analysis}</div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* TOP PERFORMERS */}
        <TabsContent value="top">
          <Card className="border shadow-none p-6 space-y-4">
            <div className="flex gap-3 flex-wrap items-end">
              <SchoolPicker value={schoolId} onChange={setSchoolId} schools={schools} t={t} lang={lang} />
              <Button onClick={runTopPerformers} disabled={!schoolId || busy} data-testid="ai-top-run">
                {busy ? <Loader2 className="w-4 h-4 animate-spin me-1" /> : <Trophy className="w-4 h-4 me-1" />}{t("show_leaderboards")}
              </Button>
              <span className="text-xs text-muted-foreground">{t("last_30_days")}</span>
            </div>

            {topPerformers && (
              <div className="space-y-4 pt-2 border-t border-border">
                {topPerformers.best_class && (
                  <Card className="p-5 border bg-gradient-to-r from-amber-50 to-emerald-50 dark:from-amber-950/30 dark:to-emerald-950/30">
                    <div className="flex items-center gap-3">
                      <Crown className="w-10 h-10 text-amber-500" />
                      <div className="flex-1">
                        <div className="text-xs text-muted-foreground">{t("best_class_of_school")}</div>
                        <div className="text-2xl font-bold">{topPerformers.best_class.class_name}</div>
                        <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                          <span>{t("avg_attendance")}: {topPerformers.best_class.avg_attendance}%</span>
                          <span>{t("avg_grade")}: {topPerformers.best_class.avg_grade}%</span>
                          <span>{t("behavior_net")}: {topPerformers.best_class.behavior_net}</span>
                        </div>
                      </div>
                      <Badge className="bg-amber-500 hover:bg-amber-500 text-lg px-3 py-1.5">
                        {topPerformers.best_class.combined_score}
                      </Badge>
                    </div>
                  </Card>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <LeaderCard title={t("top_attendance")} icon={ShieldCheck} color="emerald"
                              rows={topPerformers.attendance} unit="%" t={t} />
                  <LeaderCard title={t("top_behavior")} icon={Award} color="amber"
                              rows={topPerformers.behavior} unit="" t={t} />
                  <LeaderCard title={t("top_grades")} icon={GraduationCap} color="blue"
                              rows={topPerformers.grades} unit="%" t={t} />
                  <LeaderCard title={t("top_healthy_eaters")} icon={Apple} color="rose"
                              rows={topPerformers.healthy_eaters}
                              unit={t("points")} t={t} />
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* TEACHER EVALUATION */}
        {canEvaluateTeachers && (
        <TabsContent value="evaluation">
          <Card className="border shadow-none p-6 space-y-4">
            <div className="flex gap-3 flex-wrap items-end">
              <SchoolPicker value={schoolId} onChange={setSchoolId} schools={schools} t={t} lang={lang} />
              <Select value={teacherId} onValueChange={setTeacherId}>
                <SelectTrigger className="w-full sm:w-80" data-testid="ai-teacher-select"><SelectValue placeholder={t("select_teacher")} /></SelectTrigger>
                <SelectContent>{teachers.map((tt) => <SelectItem key={tt.id} value={tt.id}>{tt.full_name}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={runTeacherEval} disabled={!teacherId || busy} data-testid="ai-teacher-eval-run">
                {busy ? <Loader2 className="w-4 h-4 animate-spin me-1" /> : <Sparkles className="w-4 h-4 me-1" />}{t("evaluate")}
              </Button>
            </div>

            {teacherEval && (
              <div className="space-y-4 pt-2 border-t border-border">
                <Card className="p-5 border">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-xl">{teacherEval.metrics.teacher_name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {teacherEval.metrics.subjects.join(" · ") || "—"}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-primary">{teacherEval.metrics.evaluation_score ?? "—"}</div>
                      <div className="text-xs text-muted-foreground">{t("evaluation_score")} / 100</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
                    <Mini label={t("classes_count")} value={teacherEval.metrics.classes_count} />
                    <Mini label={t("students_count")} value={teacherEval.metrics.students_count} />
                    <Mini label={t("weekly_slots")} value={teacherEval.metrics.weekly_slots} />
                    <Mini label={t("avg_grade")} value={`${teacherEval.metrics.avg_grade_pct ?? "—"}%`} />
                    <Mini label={t("attendance")} value={`${teacherEval.metrics.attendance_avg_pct ?? "—"}%`} />
                  </div>
                </Card>
                <Card className="p-5 border">
                  <h4 className="font-semibold mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> {t("ai_evaluation")}</h4>
                  <div className="whitespace-pre-wrap text-sm leading-relaxed" data-testid="teacher-eval-output">{teacherEval.evaluation}</div>
                </Card>
              </div>
            )}
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function SchoolPicker({ value, onChange, schools, t, lang }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:w-64" data-testid="ai-school-select"><SelectValue placeholder={t("select_school")} /></SelectTrigger>
      <SelectContent>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function StatTile({ icon: Icon, value, label, accent }) {
  const tone = accent === "rose" ? "text-rose-600 bg-rose-50 dark:bg-rose-950/30" : "bg-muted/40";
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border border-border ${tone}`}>
      <Icon className="w-5 h-5 shrink-0" />
      <div>
        <div className="text-2xl font-bold leading-none">{value}</div>
        <div className="text-[11px] text-muted-foreground mt-1">{label}</div>
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="p-2 rounded-lg bg-muted/30 text-center">
      <div className="text-sm font-bold">{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

function LeaderCard({ title, icon: Icon, color, rows, unit, t }) {
  const borderClass = {
    emerald: "border-emerald-200 dark:border-emerald-800",
    amber:   "border-amber-200 dark:border-amber-800",
    blue:    "border-blue-200 dark:border-blue-800",
    rose:    "border-rose-200 dark:border-rose-800",
  }[color];
  const iconClass = {
    emerald: "text-emerald-600", amber: "text-amber-600",
    blue: "text-blue-600", rose: "text-rose-600",
  }[color];

  return (
    <Card className={`p-4 border ${borderClass} shadow-none`}>
      <h4 className="font-semibold flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${iconClass}`} /> {title}
      </h4>
      {(!rows || rows.length === 0) ? (
        <div className="text-center text-xs text-muted-foreground py-6">{t("no_data_yet")}</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <div key={r.student_id} className="flex items-center gap-2.5 p-2 rounded-md hover:bg-muted/30">
              <div className="w-6 h-6 rounded-full bg-muted text-foreground grid place-items-center text-xs font-bold shrink-0">
                {i === 0 ? <Crown className="w-3.5 h-3.5 text-amber-500" />
                 : i === 1 ? <Medal className="w-3.5 h-3.5 text-slate-400" />
                 : i === 2 ? <Medal className="w-3.5 h-3.5 text-amber-700" />
                 : i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{r.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{r.class_name}</div>
              </div>
              <div className={`font-bold ${iconClass}`}>{r.score}{unit && <span className="text-xs">{unit}</span>}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
